import React from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import { autofill } from 'redux-form';

import { CLIENT_CSV_FORMAT_NEW, CLIENT_CSV_FORMAT_EDIT } from 'src/constants/EndpointUrls';
import {
  DEDUCTION_ALLOWANCE_EXPORT_TYPES,
  DEDUCTION_ALLOWANCE_EXPORT_DIRECTIONS,
  ENCODING_OPTIONS
} from 'src/constants/Options';
import { CLIENT_ALLOWANCES_DEDUCTIONS_EXPORT_FORM } from 'src/constants/FormNames';
import Button from 'jbc-front/components/Button';
import ActionButton from 'jbc-front/components/ActionButton';
import { Edit, Copy, PlusCircle, Download } from 'jbc-front/components/icons';
import { Label, Section, RadioField } from 'src/jbc-front/components/Form';
import Balloon from 'src/components/Balloon';
import ExtensionSelectField from 'src/components/ExtensionSelectField';
import { Panel, Item, Headding } from 'src/components/PrimaryPanel';
import { getGroupFullPathName } from 'src/utils/Utils';
import { redirectTo, concatParamsToUrl } from 'src/utils/Http';
import { useTranslation } from 'react-i18next';
import DownloadCsvEncodingRadioButton from 'src/components/DownloadCsvEncodingRadioButton';
import styles from './AllowanceDeductionForm.scss';

const urlWithBack = (url, back) => `${url}?back=${back}`;
const editUrl = formatId => concatParamsToUrl(CLIENT_CSV_FORMAT_EDIT, { id: formatId });

const FormatGroups = ({ formatId, csvFormats, groups }) => {
  const format = csvFormats.find(fmt => fmt.id === formatId);
  const groupIds = format.groups;
  const { t } = useTranslation();

  return (
    <div className={styles.selectedGroup}>
      <Label text={t('components.AllowanceDeductionForm.selected_groups')} />
      <div className={styles.selectedGroupList}>
        {groupIds.map(grId => (
          <div key={grId} className={styles.selectedGroupItem}>
            {getGroupFullPathName(groups, grId, ' > ')}
          </div>
        ))}
      </div>
    </div>
  );
};

const AllowanceDeductionForm = props => {
  const {
    submit,
    submitting,
    pristine,
    formValues,
    mainSelectField,
    groups,
    csvFormats,
    csvType,
    back,
    copyFormat,
    dispatch,
    isCreating
  } = props;

  const { t } = useTranslation();
  const csvFormat = csvFormats.find(fmt => fmt.id === formValues.formatId);
  const csvFormatsOptions = csvFormats.filter(format => format.formatType === csvType);

  const exportTypes =
    _.isUndefined(formValues.formatId) || (csvFormat && !_.isEmpty(_.get(csvFormat, 'groups')))
      ? DEDUCTION_ALLOWANCE_EXPORT_TYPES
      : _.filter(DEDUCTION_ALLOWANCE_EXPORT_TYPES, { value: 'each_employee' });

  return (
    <form>
      <div className="l-wrap-xs">
        <Panel>
          <Headding>{t('components.AllowanceDeductionForm.provision_and_deduction_list')}</Headding>
          <Item>
            {mainSelectField()}
            <div className="u-mt20">
              <Section title={t('components.AllowanceDeductionForm.item_selection')} small>
                <div className={styles.formatSeclection}>
                  <div className={styles.formatSelextbox}>
                    <ExtensionSelectField
                      label={t('components.AllowanceDeductionForm.format')}
                      name="formatId"
                      options={csvFormatsOptions}
                      required
                      onChange={() => {
                        dispatch(autofill(CLIENT_ALLOWANCES_DEDUCTIONS_EXPORT_FORM, 'exportType', 'each_employee'));
                      }}
                    />
                  </div>
                  <div className={styles.formatActionButton}>
                    <ActionButton
                      icon={<Edit size={16} />}
                      onClick={() => redirectTo(urlWithBack(editUrl(formValues.formatId), back))}
                      disabled={!formValues.formatId}
                    >
                      {t('components.AllowanceDeductionForm.edit')}
                    </ActionButton>

                    <ActionButton
                      icon={<Copy size={16} />}
                      disabled={!formValues.formatId}
                      onClick={() => copyFormat(formValues.formatId)}
                    >
                      {t('components.AllowanceDeductionForm.copy')}
                    </ActionButton>

                    <ActionButton
                      as="a"
                      href={urlWithBack(CLIENT_CSV_FORMAT_NEW, back)}
                      icon={<PlusCircle size={16} />}
                    >
                      {t('components.AllowanceDeductionForm.create_new')}
                    </ActionButton>
                  </div>
                </div>

                {formValues.formatId && (
                  <FormatGroups formatId={formValues.formatId} csvFormats={csvFormats} groups={groups} />
                )}
              </Section>

              <Section title={t('components.AllowanceDeductionForm.export_format_selection')} small>
                <div className={styles.exportItemWrap}>
                  <RadioField name="exportType" options={exportTypes} required />
                </div>
                <div className={styles.exportItemLine}>
                  <RadioField name="exportDirection" options={DEDUCTION_ALLOWANCE_EXPORT_DIRECTIONS} required />
                </div>
                <div className={styles.exportItemWrap}>
                  <DownloadCsvEncodingRadioButton
                    name={'exportTextCode'}
                    label={t('components.AllowanceDeductionForm.character_code_selection')}
                    options={ENCODING_OPTIONS}
                    required
                  />
                </div>
              </Section>
            </div>
          </Item>

          <div className={classNames('u-ta-c', 'u-pb30', styles.downloadBtn)}>
            {isCreating && (
              <Balloon balloonPosition={'top'} arrowPosition={'center'} className={styles.downloadBtnBalloon}>
                {t('components.AllowanceDeductionForm.This_process_takes_some_time_File_will_be_sent_to_your_email_after_completed')}
              </Balloon>
            )}
            <Button
              primary
              onClick={submit}
              disabled={submitting || pristine || isCreating}
              icon={<Download size={16} />}
            >
              {t('components.AllowanceDeductionForm.download_csv')}
            </Button>
          </div>
        </Panel>
      </div>
    </form>
  );
};

export default AllowanceDeductionForm;
